/**
 * Permission keys the UI gates on.
 *
 * This is deliberately **not** a catalog of the backend's permissions — the
 * authoritative list is fetched from `GET /permissions` and rendered
 * dynamically by the RBAC management screens. This file only names the keys
 * that specific components reference, so those references are typo-proof and
 * greppable. Adding a permission to the backend needs no change here unless a
 * screen wants to gate on it.
 *
 * Convention: `<module>.<action>`, matching the backend keys.
 */

/** Build a permission key for a module resolved at runtime (e.g. CRUD configs). */
export const permissionKey = (module: string, action: string): string => `${module}.${action}`;

/**
 * Key set for a module resolved at runtime (`departments.view`,
 * `departments.manage`, …).
 *
 * Catalogs differ in how finely they slice writes: some define a single
 * `<module>.manage`, others separate `create` / `update` / `delete`. Both are
 * exposed here, and write affordances are gated on *either* (see
 * `moduleWritePermissions`) so a screen works under either convention without
 * the frontend guessing.
 */
export const crudPermissions = (module: string) =>
  ({
    view: permissionKey(module, 'view'),
    manage: permissionKey(module, 'manage'),
    create: permissionKey(module, 'create'),
    update: permissionKey(module, 'update'),
    delete: permissionKey(module, 'delete'),
    restore: permissionKey(module, 'restore'),
  }) as const;

/**
 * Write affordances for a module, each as an "any of" list: the module-wide
 * `manage` key or the specific action key. Pass the arrays to `canAny`.
 */
export const moduleWritePermissions = (module: string) => {
  const keys = crudPermissions(module);
  return {
    create: [keys.manage, keys.create],
    update: [keys.manage, keys.update],
    delete: [keys.manage, keys.delete],
    restore: [keys.manage, keys.restore],
  } as const;
};

export const PERMISSIONS = {
  dashboard: { view: 'dashboard.view' },

  /**
   * Company management.
   *
   * The verified contract gates **every** company write — create, update,
   * delete and restore, plus company addresses and bank accounts — on a single
   * `companies.manage` key. There is deliberately no `companies.create` /
   * `.update` / `.delete` / `.restore` here: the backend publishes no such keys,
   * and gating on one would lock out a user who actually holds `manage`.
   */
  companies: {
    view: 'companies.view',
    manage: 'companies.manage',
    /**
     * Reading unmasked sensitive company data — today, the full bank account
     * number behind the `/bank-accounts/:id/sensitive` route. Deliberately
     * separate from `view`: seeing that an account exists is not the same right
     * as reading its number.
     */
    viewSensitive: 'companies.view_sensitive',
  },

  /**
   * A company's bank accounts, which carry their own read key.
   *
   * Split out of `companies.view`: seeing a company is not the same right as
   * seeing where it banks, and every role that could read a company — sales,
   * delivery, marketing, the HR assistant — could list its account holder,
   * bank, branch, IFSC and masked number until that split.
   *
   * Three levels, none implied by the others: `view` here (the account exists,
   * masked), `companies.view_sensitive` (the full number), `companies.manage`
   * (create, edit, delete).
   */
  companyBankAccounts: {
    view: 'company-bank-accounts.view',
  },

  /**
   * Company locations carry their own permission module in the contract — they
   * are **not** gated on the parent company's keys, unlike company addresses and
   * bank accounts.
   */
  companyLocations: {
    view: 'company-locations.view',
    manage: 'company-locations.manage',
  },

  /**
   * Standalone address records. The link between an address and its owner is
   * gated by the owning module; editing the address's own fields is gated here.
   */
  addresses: {
    view: 'addresses.view',
    manage: 'addresses.manage',
  },

  employees: {
    view: 'employees.view',
    create: 'employees.create',
    update: 'employees.update',
    delete: 'employees.delete',
    /**
     * Reading unmasked employee data. Backs `GET /employees/:id/sensitive` and
     * `GET /employees/:employeeId/past-employment/:employmentId/sensitive`.
     * Deliberately separate from `view`: seeing a record is not the same right
     * as reading the values it masks.
     */
    viewSensitive: 'employees.view_sensitive',
  },

  /**
   * Employee documents carry their own permission keys — unlike most Employee
   * submodules, which are gated on `employees.view` / `employees.update`.
   *
   * `manage` covers create, update, delete **and** restore; `verify` is a
   * separate right, because accepting or rejecting a document is not the same
   * as attaching one.
   */
  employeeDocuments: {
    view: 'employee-documents.view',
    manage: 'employee-documents.manage',
    verify: 'employee-documents.verify',
  },

  /**
   * Employee skills carry their own permission keys — unlike the other Employee
   * submodules, which are gated on `employees.view` / `employees.update`.
   */
  employeeSkills: {
    view: 'employee-skills.view',
    manage: 'employee-skills.manage',
  },

  /**
   * Employee separations. `employee-exits.view` is an existing backend key —
   * only the read is named here, because the profile only reads: the exit
   * workflow itself belongs to the Employee Exit module.
   */
  employeeExits: {
    view: 'employee-exits.view',
    create: 'employee-exits.create',
    manage: 'employee-exits.manage',
    approve: 'employee-exits.approve',
  },

  attendance: {
    view: 'attendance.view',
    /** Generate / regenerate (the Attendance Engine runs server-side). */
    manage: 'attendance.manage',
  },

  holidays: {
    view: 'holidays.view',
    manage: 'holidays.manage',
  },

  leaveTypes: {
    view: 'leave-types.view',
    manage: 'leave-types.manage',
  },
  leavePolicies: {
    view: 'leave-policies.view',
    manage: 'leave-policies.manage',
  },

  /**
   * Leave. Mirrors the backend catalogue exactly — `leave-balances.view` and
   * `leave-requests.view` are existing keys there, not new ones invented here.
   * Only the read keys are named, because only reads are surfaced from the
   * employee profile; the Leave module owns its own writes.
   */
  leaveBalances: {
    view: 'leave-balances.view',
    adjust: 'leave-balances.adjust',
  },
  leaveRequests: {
    view: 'leave-requests.view',
    create: 'leave-requests.create',
    update: 'leave-requests.update',
    approve: 'leave-requests.approve',
  },

  /**
   * Compensation — what an employee is ENTITLED to: the salary structure, its
   * revisions, the salary history and the adjustments booked against it.
   *
   * Its own permission module in the backend catalogue. These routes used to be
   * guarded by `employees.view` / `employees.update`, which made administering
   * an employee record and seeing what they are paid the same right; they are
   * now separate keys, so a role can hold one without the other. Gate every
   * salary surface on these — never on `employees.*`.
   */
  compensation: {
    view: 'compensation.view',
    manage: 'compensation.manage',
  },

  /**
   * Payroll — what is actually RUN and PAID: payroll runs, employee payment
   * records and their payment transactions. Separate from `compensation`
   * because processing a payroll and defining a salary are different
   * responsibilities and the backend now grants them independently.
   */
  payroll: {
    view: 'payroll.view',
    manage: 'payroll.manage',
  },

  /**
   * Clients (customers).
   *
   * The backend gates every client write — create, update, delete, restore and
   * the client's addresses — on the single `clients.manage` key, the way
   * companies do. There is deliberately no `clients.create` / `.update` /
   * `.delete` here: no such key exists in the catalogue, and gating on one
   * would lock out a user who actually holds `manage`.
   */
  clients: {
    view: 'clients.view',
    manage: 'clients.manage',
  },

  /**
   * Invoicing.
   *
   * These are the backend catalogue's OWN keys, read from
   * `permission-catalog.ts` — not guessed from the UI's shape. Two points
   * where a reasonable guess would have been wrong:
   *
   *   * The invoice itself has SPLIT actions (`create` / `update` / `delete` /
   *     `send` / `cancel`), not a single `manage`. Issuing a document to a
   *     customer and voiding one already issued are separate rights.
   *   * Payments are `invoice-payments.*`, a resource of their own — NOT
   *     `invoices.payment.*`. The convention is two segments,
   *     `<resource>.<action>`, and nothing in the catalogue has three.
   *
   * Everything here is UX only. The backend re-checks every request, and a
   * hidden button is not a permission.
   */
  invoices: {
    view: 'invoices.view',
    create: 'invoices.create',
    update: 'invoices.update',
    delete: 'invoices.delete',
    send: 'invoices.send',
    cancel: 'invoices.cancel',
    export: 'invoices.export',
  },
  invoicePayments: {
    view: 'invoice-payments.view',
    create: 'invoice-payments.create',
  },
  invoiceNumbering: {
    view: 'invoice-numbering.view',
    manage: 'invoice-numbering.manage',
  },
  invoiceTemplates: {
    view: 'invoice-templates.view',
    manage: 'invoice-templates.manage',
  },
  invoiceItems: {
    view: 'invoice-items.view',
    manage: 'invoice-items.manage',
  },
  /**
   * Finance & Accounts. EMPLOYEE-GRANTABLE on the backend: held through a
   * role (the baseline) and/or an individual employee grant, and not covered
   * by the owner bypass (the backend sends the list in
   * `employeeGrantablePermissions`, and `checkPermission` honours it). These
   * are key NAMES for gating — who holds them comes only from `/auth/me`.
   */
  finance: {
    access: 'finance.access',
    view: 'finance.view',
    manage: 'finance.manage',
    approve: 'finance.approve',
    /** Grant/revoke finance access for other employees. */
    manageAccess: 'finance-access.manage',
    /** View / reveal / manage asset device credentials. */
    assetCredentials: 'finance.asset-credentials',
    /** Download the SENSITIVE credential export. */
    assetCredentialsExport: 'finance.asset-credentials.export',
  },

  invoiceTaxes: {
    view: 'invoice-taxes.view',
    manage: 'invoice-taxes.manage',
  },

  /** Restore is `PATCH /projects/:id/restore`, gated on `projects.update`. */
  projects: {
    view: 'projects.view',
    create: 'projects.create',
    update: 'projects.update',
    delete: 'projects.delete',
  },
  projectStatuses: {
    view: 'project-statuses.view',
    manage: 'project-statuses.manage',
  },
  projectRoles: {
    view: 'project-roles.view',
    manage: 'project-roles.manage',
  },
  moduleStatuses: {
    view: 'module-statuses.view',
    manage: 'module-statuses.manage',
  },
  projectSkills: {
    view: 'project-skills.view',
    manage: 'project-skills.manage',
  },
  projectMappings: {
    view: 'project-mappings.view',
    manage: 'project-mappings.manage',
  },

  projectMembers: {
    view: 'project-members.view',
    manage: 'project-members.manage',
  },

  projectModules: {
    view: 'project-modules.view',
    manage: 'project-modules.manage',
  },

  reports: {
    view: 'reports.view',
    manage: 'reports.manage',
    export: 'reports.export',
  },

  teamlogger: {
    view: 'teamlogger.view',
    sync: 'teamlogger.sync',
    manage: 'teamlogger.manage',
  },

  asana: {
    view: 'asana.view',
    manage: 'asana.manage',
  },

  export: {
    generate: 'export.generate',
  },

  assets: {
    view: 'assets.view',
    manage: 'assets.manage',
    assign: 'assets.assign',
  },
  assetCategories: {
    view: 'asset-categories.view',
    manage: 'asset-categories.manage',
  },
  vendorCategories: {
    view: 'vendor-categories.view',
    manage: 'vendor-categories.manage',
  },

  auditLogs: {
    view: 'audit-logs.view',
  },

  applications: {
    assign: 'applications.assign',
  },

  files: {
    view: 'files.view',
    upload: 'files.upload',
    delete: 'files.delete',
  },

  roles: {
    view: 'roles.view',
    create: 'roles.create',
    update: 'roles.update',
    delete: 'roles.delete',
    assign: 'roles.assign',
  },

  permissions: {
    view: 'permissions.view',
    manage: 'permissions.manage',
  },

  /**
   * Login accounts.
   *
   * Reading an account is `users.view`. **Changing** one — and creating one
   * through `/provisioning/*` — is `provisioning.manage`, so holding
   * `users.view` alone must never render an edit affordance. There is no
   * `users.create` / `users.update` key in the backend catalogue.
   */
  users: {
    view: 'users.view',
    /** Account writes and provisioning: PATCH /users/:id, /provisioning/*. */
    manage: 'provisioning.manage',
    /** Company assignment for an account: GET/PUT /users/:id/companies. */
    assignCompanies: 'companies.assign',
  },
} as const;

/**
 * Permissions that open the Access Control area — the sidebar section, the
 * Roles menu item and the Roles management page.
 *
 * Deliberately **excludes `roles.view`**: every authenticated user holds that
 * key because `GET /roles` backs the role pickers used across the app, so it
 * says nothing about administering roles. It also excludes `roles.assign`,
 * which grants attaching existing roles to a user — not managing the roles
 * themselves.
 *
 * Listed as an "any of" set because catalogues differ: some define a single
 * `roles.manage`, others separate create/update/delete keys. Holding any one of
 * them means the user administers roles.
 */
export const ROLES_MANAGEMENT_PERMISSIONS: readonly string[] = [
  PERMISSIONS.roles.create,
  PERMISSIONS.roles.update,
  PERMISSIONS.roles.delete,
];

/**
 * Who may open Employee → Permissions — ANY of these, the same set the backend
 * accepts on `GET /employees/:id/permissions`. Reading an account's roles
 * (`users.view`, `roles.assign`) or managing employee-specific grants
 * (`finance-access.manage`). The backend still decides, per employee, which
 * half of the screen the caller may see.
 */
export const EMPLOYEE_PERMISSIONS_VIEW_KEYS: readonly string[] = [
  PERMISSIONS.users.view,
  PERMISSIONS.roles.assign,
  PERMISSIONS.finance.manageAccess,
];

/**
 * Permission keys are plain strings: modules resolve some keys at runtime (the
 * Organization CRUD configs, the RBAC screens) and the backend may add new keys
 * at any time, so a closed union would be wrong here.
 */
export type PermissionKey = string;
